import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
    Activity, Zap, ShieldCheck, Users, Radio, Loader2, RefreshCw,
    Trophy, Send, TrendingUp, MessageSquare, AlertTriangle, HelpCircle,
} from "lucide-react";
import { toast } from "sonner";


const StatCard = ({ label, value, tone = "text-white", sub, Icon, testid }) => (
    <Card className="bg-paper border-border p-4" data-testid={testid}>
        <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-heading">{label}</span>
            {Icon && <Icon className="h-3.5 w-3.5 text-white/40" />}
        </div>
        <div className={`text-2xl font-heading font-black tabular-nums ${tone}`}>{value}</div>
        {sub && <div className="text-[11px] text-white/40 mt-0.5">{sub}</div>}
    </Card>
);

const LiveOps = () => {
    const { token } = useAuth();
    const authH = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async (initial = false) => {
        if (initial) setLoading(true); else setRefreshing(true);
        try {
            const r = await axios.get(`${API}/admin/live-ops/overview`, { headers: authH, timeout: 30000 });
            setData(r.data);
        } catch (e) {
            toast.error("Overview load failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [authH]);

    useEffect(() => { load(true); }, [load]);
    useEffect(() => {
        const t = setInterval(() => load(false), 20000);   // auto-refresh every 20s
        return () => clearInterval(t);
    }, [load]);

    const flipMaster = async (val) => {
        try {
            await axios.put(`${API}/admin/auto-sms/settings`, { enabled: val }, { headers: authH });
            toast.success(val ? "Auto-SMS enabled" : "Auto-SMS paused");
            load(false);
        } catch (e) { toast.error("Toggle failed", { description: e?.response?.data?.detail }); }
    };

    const settings = data?.settings || {};
    const live = data?.live_matches || [];
    const auto = data?.auto_sms || {};
    const approvals = data?.approvals || {};
    const today = data?.today || {};
    const reply1Topics = data?.reply1_topics || [];

    if (loading) {
        return (
            <AdminLayout title="Live Ops">
                <div className="flex items-center justify-center py-32 text-white/40" data-testid="live-ops-loading">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading live overview…
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Live Ops">
            <div className="space-y-6" data-testid="live-ops-page">
                {/* Header */}
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-heading text-3xl font-black text-white flex items-center gap-3">
                            <Activity className="h-7 w-7 text-emerald-400" />
                            Live Ops Dashboard
                        </h1>
                        <p className="text-sm text-white/50 mt-1 max-w-2xl">
                            One-glance view of live matches, real-time auto-SMS activity, pending approvals, and today&apos;s delivery stats. Auto-refreshes every 20 s.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button variant="outline" size="sm" onClick={() => load(false)} disabled={refreshing} className="border-border text-white" data-testid="live-ops-refresh">
                            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
                            {refreshing ? "Refreshing…" : "Refresh"}
                        </Button>
                    </div>
                </div>

                {/* Master switch bar */}
                <Card className={`p-5 border ${settings.auto_sms_enabled ? "bg-gradient-to-r from-emerald-400/[0.06] to-paper border-emerald-400/40" : "bg-gradient-to-r from-orange-400/[0.06] to-paper border-orange-400/40"}`}>
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                            <Zap className={settings.auto_sms_enabled ? "h-8 w-8 text-emerald-300" : "h-8 w-8 text-orange-300"} />
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-heading">Auto-SMS Master Switch</div>
                                <div className="mt-0.5 text-xl text-white font-heading font-bold flex items-center gap-2">
                                    {settings.auto_sms_enabled ? "LIVE" : "PAUSED"}
                                    <Badge className={settings.auto_sms_enabled
                                        ? "bg-emerald-400/20 text-emerald-300 border-emerald-400/40 border text-[10px]"
                                        : "bg-orange-400/20 text-orange-300 border-orange-400/40 border text-[10px]"}>
                                        {settings.enabled_rules}/{settings.total_rules} rules active
                                    </Badge>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-white/50">Kill switch</span>
                            <Switch checked={!!settings.auto_sms_enabled} onCheckedChange={flipMaster} data-testid="live-ops-master-switch" />
                        </div>
                    </div>
                </Card>

                {/* Top-line counters */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard
                        label="Live matches"
                        value={live.length}
                        Icon={Radio}
                        tone="text-white"
                        sub={live.length ? live[0].league : "None currently live"}
                        testid="stat-live"
                    />
                    <StatCard
                        label="Auto-SMS · last hour"
                        value={auto.last_hour_events || 0}
                        Icon={Zap}
                        tone="text-emerald-300"
                        sub={`${auto.last_24h_events || 0} in 24h · reach ${auto.last_24h_reach || 0}`}
                        testid="stat-auto"
                    />
                    <StatCard
                        label="Pending approvals"
                        value={approvals.pending_count || 0}
                        Icon={ShieldCheck}
                        tone={approvals.pending_count > 5 ? "text-orange-300" : "text-white"}
                        sub={approvals.revisions_count ? `${approvals.revisions_count} in revisions` : "Inbox healthy"}
                        testid="stat-approvals"
                    />
                    <StatCard
                        label="Today · delivered"
                        value={today.delivered || 0}
                        Icon={Send}
                        tone="text-electric"
                        sub={today.delivery_rate != null ? `${today.delivery_rate}% delivery rate` : `${today.campaigns_sent || 0} sent`}
                        testid="stat-delivered"
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* Live matches column */}
                    <Card className="bg-paper border-border p-5 lg:col-span-2">
                        <div className="flex items-center gap-2 mb-4">
                            <Trophy className="h-4 w-4 text-yellow-300" />
                            <h2 className="font-heading text-lg font-bold text-white">Live Matches · {live.length}</h2>
                            <span className="text-[10px] text-white/40 ml-auto">Auto-SMS activity per fixture</span>
                        </div>
                        {live.length === 0 ? (
                            <div className="text-center py-12 text-white/40 text-sm" data-testid="no-live-matches">
                                No matches currently live. Alerts will appear here as soon as kickoff begins.
                            </div>
                        ) : (
                            <div className="space-y-2" data-testid="live-matches-list">
                                {live.map((m) => (
                                    <div key={m.id} className="p-3 rounded border border-border bg-obsidian" data-testid={`live-match-${m.id}`}>
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                                                {m.home_logo && <img src={m.home_logo} alt="" className="w-6 h-6 object-contain" onError={(e)=>{e.currentTarget.style.display="none";}} />}
                                                <span className="text-white font-bold text-sm">{m.home_team}</span>
                                                <span className="text-white/40 text-xs">vs</span>
                                                {m.away_logo && <img src={m.away_logo} alt="" className="w-6 h-6 object-contain" onError={(e)=>{e.currentTarget.style.display="none";}} />}
                                                <span className="text-white font-bold text-sm">{m.away_team}</span>
                                            </div>
                                            {m.score && <Badge className="bg-yellow-300/20 text-yellow-300 border-yellow-300/40 border text-[11px] font-mono">{m.score}</Badge>}
                                            {m.clock && <Badge variant="outline" className="text-[10px] border-white/15 text-white/60">{m.clock}</Badge>}
                                            <Badge variant="outline" className="text-[10px] border-emerald-400/40 text-emerald-300">
                                                {m.auto_sms?.sends || 0} auto-sms · {m.auto_sms?.rule_count || 0} rules
                                            </Badge>
                                        </div>
                                        <div className="mt-1.5 text-[11px] text-white/40">{m.league}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                    {/* Approvals column */}
                    <Card className="bg-paper border-border p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <ShieldCheck className="h-4 w-4 text-electric" />
                            <h2 className="font-heading text-lg font-bold text-white">Approvals</h2>
                            <Link to="/admin/approvals" className="ml-auto text-[11px] text-electric hover:underline" data-testid="link-approvals">
                                Open queue →
                            </Link>
                        </div>
                        {approvals.pending_count === 0 && approvals.revisions_count === 0 ? (
                            <div className="text-center py-8 text-white/40 text-sm">Inbox zero — no pending campaigns.</div>
                        ) : (
                            <div className="space-y-2" data-testid="approvals-preview">
                                <div className="grid grid-cols-2 gap-2 mb-2">
                                    <div className="text-center p-2 rounded bg-obsidian border border-border">
                                        <div className="text-2xl font-heading font-black text-yellow-300 tabular-nums">{approvals.pending_count}</div>
                                        <div className="text-[10px] text-white/50 uppercase tracking-wider">Pending</div>
                                    </div>
                                    <div className="text-center p-2 rounded bg-obsidian border border-border">
                                        <div className="text-2xl font-heading font-black text-orange-300 tabular-nums">{approvals.revisions_count}</div>
                                        <div className="text-[10px] text-white/50 uppercase tracking-wider">Revisions</div>
                                    </div>
                                </div>
                                {(approvals.oldest_pending || []).map((c) => (
                                    <Link
                                        key={c.id}
                                        to="/admin/approvals"
                                        className="block p-2.5 rounded border border-border bg-obsidian hover:border-electric/40 transition-colors"
                                        data-testid={`pending-preview-${c.id}`}
                                    >
                                        <div className="text-sm text-white font-medium truncate">{c.name}</div>
                                        <div className="text-[11px] text-white/50 line-clamp-2 mt-0.5 font-mono">{c.content}</div>
                                        <div className="text-[10px] text-white/40 mt-1">{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </Card>
                </div>

                {/* Recent auto-SMS + today totals */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <Card className="bg-paper border-border p-5 lg:col-span-2">
                        <div className="flex items-center gap-2 mb-4">
                            <Zap className="h-4 w-4 text-emerald-300" />
                            <h2 className="font-heading text-lg font-bold text-white">Recent Auto-SMS · last hour</h2>
                            <Link to="/admin/auto-sms" className="ml-auto text-[11px] text-electric hover:underline" data-testid="link-auto-sms">
                                Manage rules →
                            </Link>
                        </div>
                        {(auto.last_hour_activity || []).length === 0 ? (
                            <div className="text-center py-8 text-white/40 text-sm">No events fired in the last hour.</div>
                        ) : (
                            <div className="max-h-80 overflow-y-auto" data-testid="auto-sms-recent">
                                <table className="w-full text-sm">
                                    <thead className="sticky top-0 bg-paper">
                                        <tr className="text-[10px] uppercase tracking-wider text-white/40 font-heading border-b border-border">
                                            <th className="text-left p-2">Sent</th>
                                            <th className="text-left p-2">Event</th>
                                            <th className="text-right p-2">Reach</th>
                                            <th className="text-left p-2">Message</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(auto.last_hour_activity || []).map((r) => (
                                            <tr key={r.id} className="border-b border-border/40 hover:bg-white/[0.02]">
                                                <td className="p-2 text-white/60 text-[11px]">{new Date(r.sent_at).toLocaleTimeString()}</td>
                                                <td className="p-2">
                                                    <Badge className="bg-yellow-300/15 text-yellow-300 border-yellow-300/30 border text-[10px]">{r.event_type}</Badge>
                                                </td>
                                                <td className="p-2 text-right text-white tabular-nums">{r.phones_count}</td>
                                                <td className="p-2 text-white/70 text-xs truncate max-w-[260px]" title={r.message}>{r.message}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>

                    <Card className="bg-paper border-border p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <TrendingUp className="h-4 w-4 text-electric" />
                            <h2 className="font-heading text-lg font-bold text-white">Today&apos;s Totals</h2>
                        </div>
                        <div className="space-y-3" data-testid="today-totals">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-white/60 flex items-center gap-2"><Send className="h-3.5 w-3.5" /> Campaigns sent</span>
                                <span className="font-heading font-bold text-white tabular-nums">{today.campaigns_sent || 0}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-white/60 flex items-center gap-2"><TrendingUp className="h-3.5 w-3.5 text-emerald-300" /> Delivered</span>
                                <span className="font-heading font-bold text-emerald-300 tabular-nums">{today.delivered || 0}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-white/60 flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5 text-orange-300" /> Failed</span>
                                <span className="font-heading font-bold text-orange-300 tabular-nums">{today.failed || 0}</span>
                            </div>
                            <div className="pt-2 border-t border-border">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-white/60 flex items-center gap-2"><Zap className="h-3.5 w-3.5" /> Auto push-events</span>
                                    <span className="font-heading font-bold text-white tabular-nums">{today.push_events || 0}</span>
                                </div>
                                <div className="flex items-center justify-between mt-1">
                                    <span className="text-sm text-white/60 flex items-center gap-2"><Users className="h-3.5 w-3.5" /> Auto push-reach</span>
                                    <span className="font-heading font-bold text-white tabular-nums">{today.push_reach || 0}</span>
                                </div>
                            </div>
                            <div className="pt-2 border-t border-border">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-white/60 flex items-center gap-2"><MessageSquare className="h-3.5 w-3.5" /> Inbound chats</span>
                                    <span className="font-heading font-bold text-white tabular-nums">{today.inbound_chats || 0}</span>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Top "Reply 1" topics — the questions that made customers ask for more depth */}
                <Card className="bg-paper border-border p-5" data-testid="reply1-topics-card">
                    <div className="flex items-center gap-2 mb-1.5">
                        <HelpCircle className="h-4 w-4 text-yellow-300" />
                        <h2 className="font-heading text-lg font-bold text-white">Top &quot;Reply 1&quot; Topics · last 7 days</h2>
                        <span className="text-[10px] text-white/40 ml-auto">what deserves richer content</span>
                    </div>
                    <p className="text-[11px] text-white/50 mb-4">
                        Every SMS reply ends with <span className="font-mono text-white/70">Jibu 1 kupata taarifa zaidi</span>. When customers reply <span className="font-mono text-white/70">1</span>, they&apos;re telling you the topic needs more depth. Prioritize these in the Content Hub.
                    </p>
                    {reply1Topics.length === 0 ? (
                        <div className="text-center py-8 text-white/40 text-sm" data-testid="reply1-empty">
                            No follow-up requests yet. Once customers start replying &quot;1&quot;, their trigger questions will surface here.
                        </div>
                    ) : (
                        <div className="overflow-x-auto" data-testid="reply1-topics-list">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-wider text-white/40 font-heading border-b border-border">
                                        <th className="text-left p-2 w-8">#</th>
                                        <th className="text-left p-2">Trigger question</th>
                                        <th className="text-right p-2">Reply-1 count</th>
                                        <th className="text-right p-2">Unique phones</th>
                                        <th className="text-right p-2">Last</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reply1Topics.map((t, i) => (
                                        <tr key={`${t.topic}-${i}`} className="border-b border-border/40 hover:bg-white/[0.02]" data-testid={`reply1-row-${i}`}>
                                            <td className="p-2 text-white/40 font-mono text-xs tabular-nums">{i + 1}</td>
                                            <td className="p-2 text-white/85 max-w-[520px]">
                                                <div className="truncate" title={t.topic}>{t.topic}</div>
                                            </td>
                                            <td className="p-2 text-right">
                                                <Badge className="bg-yellow-300/15 text-yellow-300 border-yellow-300/40 border text-[10px] font-mono tabular-nums">
                                                    {t.count}
                                                </Badge>
                                            </td>
                                            <td className="p-2 text-right text-white/70 tabular-nums font-mono text-xs">{t.unique_phone_count}</td>
                                            <td className="p-2 text-right text-white/40 text-[11px]">{t.latest_at ? new Date(t.latest_at).toLocaleString() : "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>
        </AdminLayout>
    );
};

export default LiveOps;
